import { useState } from 'react';
import { api, loadSession, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import NewItemButton from '../components/NewItemButton';

export default function Transfers() {
  const list = useLoad(() => api<any[]>('/api/transfers'), []);
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Transfers</h1><p>Move stock between your stores. The sending store's stock drops right away; the receiving store adds it when it arrives. Cost moves with the bottles.</p></div>
        <button className="primary" onClick={() => setCreating(true)}>Send stock</button>
      </div>
      <ErrorNote error={list.error} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Direction</th><th>From</th><th>To</th><th className="n">Units</th><th className="n">Cost</th><th>Status</th><th>Sent</th></tr></thead>
          <tbody>{list.data?.map((t) => (
            <tr key={t.id} className="clickable" onClick={() => setOpen(t.id)}>
              <td>{t.direction === 'out' ? 'Sent' : 'Incoming'}</td><td>{t.from_name}</td><td>{t.to_name}</td>
              <td className="n">{t.units}</td><td className="n">{usd(t.value_cents)}</td>
              <td><span className={`chip ${t.status === 'received' ? 'good' : t.status === 'in_transit' ? 'warn' : ''}`}>{t.status.replace('_', ' ')}</span></td>
              <td>{new Date(t.created_at).toLocaleDateString()}</td>
            </tr>))}</tbody>
        </table>
        {list.data?.length === 0 && <div className="empty">No transfers yet. You need at least two stores to move stock.</div>}
      </div></div>
      {creating && <NewTransfer onClose={() => setCreating(false)} onDone={() => { setCreating(false); list.reload(); }} />}
      {open && <TransferDetail id={open} onClose={() => { setOpen(null); list.reload(); }} />}
    </div>
  );
}

function NewTransfer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const session = loadSession();
  const stores = useLoad(() => api<any[]>('/api/stores'), []);
  const others = (stores.data ?? []).filter((s) => s.id !== session?.storeId);
  const [to, setTo] = useState('');
  const [find, setFind] = useState('');
  const found = useLoad(() => (find.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(find)}&limit=8`) : Promise.resolve([])), [find]);
  const [lines, setLines] = useState<{ item: any; qty: string }[]>([]);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const valid = to && lines.length > 0 && lines.every((l) => Number(l.qty) > 0);
  return (
    <Modal title="Send stock to another store" onClose={onClose} wide footer={<button className="primary" disabled={!valid} onClick={async () => {
      try { await api('/api/transfers', { body: { toStoreId: to, note: note || undefined, lines: lines.map((l) => ({ itemId: l.item.id, qty: Number(l.qty) })) } }); onDone(); } catch (e) { setErr(e); }
    }}>Send</button>}>
      <ErrorNote error={err} />
      <label>Send to<select value={to} onChange={(e) => setTo(e.target.value)}>
        <option value="">Choose a store</option>{others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select></label>
      {stores.data && others.length === 0 && <div className="notice">You only have one store. Add another store first.</div>}
      <label>Add an item<input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search your items" /></label>
      {found.data?.filter((i) => !lines.some((l) => l.item.id === i.id)).map((i) => (
        <button key={i.id} className="ghost" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => { setLines([...lines, { item: i, qty: '1' }]); setFind(''); }}>
          {i.brand} {i.name} · {sizeLabel(i.size_ml)} <span className="faint">({i.on_hand} on hand)</span></button>))}
      <div><NewItemButton onCreated={(i) => { setLines([...lines, { item: i, qty: '1' }]); setFind(''); }} /></div>
      {lines.length > 0 && (
        <table className="grid"><tbody>{lines.map((l, i) => (
          <tr key={l.item.id}>
            <td>{l.item.brand} {l.item.name} <span className="faint">{sizeLabel(l.item.size_ml)} · {l.item.on_hand} on hand</span></td>
            <td className="n"><input style={{ width: 70, textAlign: 'right' }} inputMode="numeric" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/\D/g, '') } : x)))} /></td>
            <td><button className="ghost danger" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button></td>
          </tr>))}</tbody></table>
      )}
      <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
    </Modal>
  );
}

function TransferDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useLoad(() => api<any>(`/api/transfers/${id}`), [id]);
  const [recv, setRecv] = useState<Record<string, string>>({});
  const [err, setErr] = useState<unknown>(null);
  const d = t.data;
  const canAct = d?.status === 'in_transit';
  async function receive() {
    setErr(null);
    try {
      const received: Record<string, number> = {};
      for (const l of d.lines) if (recv[l.id] !== undefined && recv[l.id] !== '') received[l.id] = Number(recv[l.id]);
      await api(`/api/transfers/${id}/receive`, { body: { received } }); t.reload();
    } catch (e) { setErr(e); }
  }
  return (
    <Modal title={d ? `${d.from_name} → ${d.to_name}` : 'Transfer'} onClose={onClose} wide footer={
      d && canAct && (d.direction === 'in'
        ? <button className="primary" onClick={receive}>Receive stock</button>
        : <button onClick={async () => { if (confirm('Take this back? The stock returns to your store.')) { try { await api(`/api/transfers/${id}/cancel`, { body: {} }); t.reload(); } catch (e) { setErr(e); } } }}>Cancel and take back</button>)}>
      <ErrorNote error={err ?? t.error} />
      {d && (
        <>
          <p><span className={`chip ${d.status === 'received' ? 'good' : d.status === 'in_transit' ? 'warn' : ''}`}>{d.status.replace('_', ' ')}</span> {d.note}</p>
          <table className="grid">
            <thead><tr><th>Product</th><th className="n">Sent</th><th className="n">Received</th><th className="n">Cost</th></tr></thead>
            <tbody>{d.lines.map((l: any) => (
              <tr key={l.id}>
                <td>{l.brand} {l.name} <span className="faint">{sizeLabel(l.size_ml)}</span></td>
                <td className="n">{l.qty}</td>
                <td className="n">{canAct && d.direction === 'in'
                  ? <input style={{ width: 70, textAlign: 'right' }} inputMode="numeric" placeholder={String(l.qty)} value={recv[l.id] ?? ''} onChange={(e) => setRecv({ ...recv, [l.id]: e.target.value.replace(/\D/g, '') })} />
                  : l.qty_received ?? '—'}</td>
                <td className="n">{usd(l.value_cents)}</td>
              </tr>))}</tbody>
          </table>
          {canAct && d.direction === 'in' && <p className="faint">Leave Received blank if everything arrived. Anything short is booked as shrink.</p>}
        </>
      )}
    </Modal>
  );
}
